/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint max-depth: [error,7] */
import type { EntityTypeMap } from 'types/entity';
import { ValidationResult } from 'types/routes';
import { messages } from '@redviking/argonaut-core-ui/src/i18n/i18n';
import { appletLibPath, appletVarsPath, generateAppletVarLib, getReferencedAppletVars } from '@redviking/argonaut-util/src/argo-lang/parser';
import {
    flowsTabRoute,
    generalTabRoute,
    screensTabRoute,
    scriptsTabRoute,
} from '../util/consts';
import macroTypeMap from '../macros/macroTypeMap';
import {
    type Latest,
    appletDesignVersionConfigSchemaLatest,
} from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';
import { getUnlinkedCell } from './unlinked-cells';
import { getAppletEvents } from '@redviking/argonaut-util/src/mes/get-applet-events';
import { genericScreenDesignerCellValidations } from './screen-designer.validations';
import { NodeValidation, nodeKindMap } from '../flows/nodes/specs';
import { NodeCfg, type NodeKind, type NodeSocketKind, type NodeSocketKindMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import * as monaco from 'monaco-editor/esm/vs/editor/editor.api.js';
import { setExtraLibs } from 'src/util/composables/use-monaco-editor';
import appletScriptLibMd from 'raw-loader!@redviking/argonaut-util/src/argo-lang/applet-script-lib.md';
import { argoScriptModel, argoScriptUri } from '../util/AppletScriptEditor.vue';
import type { PendingMediaObjectReference } from '@redviking/argonaut-util/src/media.util';
import type { NodeConnections, RequiredSockets, UiNodeSpec, WideNodeConnections } from '@redviking/argonaut-core-ui/src/applet/design/flows/nodes/specs';
import { findCellInCfg, getAllAppletVariables } from '@redviking/argonaut-util/src/mes/appletUtil';
import cellTypeMap from '@redviking/argonaut-core-ui/src/applet/design/cells/cellTypeMap';
import type { MediaUploadDialogPayload } from '../../../media/MediaUploadDialog.vue';
import {
    type DeployParamKind,
    getDeployParams,
} from '@redviking/argonaut-util/src/mes/deployParam.zod';
import { UiFlowNodeSocket } from '@redviking/argonaut-core-ui/src/applet/design/flows/sockets/UiFlowNodeSocket';
import type { UiFlowNodeInput } from '../flows/sockets/UiFlowNodeInput';
import type { UiFlowNodeOutput } from '../flows/sockets/UiFlowNodeOutput';
import type { FeatureFlags } from '@redviking/argonaut-util/src/feature-flags';

// If applet-script-lib is a .d.ts, then the ts-loader includes it in type checking, which breaks a lot of things.
// To avoid this I wrapped it in a markdown file so we still have syntax highlighting
const appletScriptLib = appletScriptLibMd.replace(/```.*/ug, '');

const appletLibUri = monaco.Uri.parse(appletLibPath);
const appletVarsUri = monaco.Uri.parse(appletVarsPath);

export function getAppletScriptExtraLibs (allAppletVariables: Record<string, Latest.AppletVariables.AppletVariable | Latest.AppletVariables.GeneratedAppletVariable>) {
    const extraLibs: { content: string, filePath: string }[] = [];
    if (appletScriptLib) {
        if (Object.keys(allAppletVariables).length > 0) {
            extraLibs.push({
                content: generateAppletVarLib(allAppletVariables),
                filePath: appletVarsUri.toString(),
            });
        }

        extraLibs.push({ content: appletScriptLib, filePath: appletLibUri.toString() });
    }
    return extraLibs;
}

async function validateJavascript (content: string, allAppletVariables: Record<string, Latest.AppletVariables.AppletVariable | Latest.AppletVariables.GeneratedAppletVariable>) {
    // preserve the original script and libs
    const originalScript = argoScriptModel.getValue();
    const originalExtraLibsRecord = monaco.languages.typescript.javascriptDefaults.getExtraLibs();
    const originalExtraLibs = Object.entries(originalExtraLibsRecord).map(([ filePath, extraLib ]) => ({
        content: extraLib.content,
        filePath,
    }));

    // set the script to validate
    argoScriptModel.setValue(content);

    setExtraLibs(getAppletScriptExtraLibs(allAppletVariables));

    const lspWorker = await monaco.languages.typescript.getJavaScriptWorker().then(workerFn => workerFn(argoScriptUri));

    // prompt the worker to validate the script, we'll use model markers though because they're more accurate immediately.
    await lspWorker.getSyntacticDiagnostics(argoScriptUri.toString());
    await lspWorker.getSemanticDiagnostics(argoScriptUri.toString());
    const modelMarkers = monaco.editor.getModelMarkers({ resource: argoScriptUri }).filter(mm => mm.severity === monaco.MarkerSeverity.Error);

    // restore the original script and libs
    argoScriptModel.setValue(originalScript);
    monaco.languages.typescript.javascriptDefaults.setExtraLibs(originalExtraLibs);

    return { modelMarkers };
}

const validateScriptsAccessorActions = (accessor: Parameters<NonNullable<(ValidationResult & { status: 'error'})['accessorActions']>>[0], scriptId: string) => {
    accessor.appletDesign.setValidatingScripts(false);
    accessor.appletDesign.patchAppletDesignState({ activeScriptId: scriptId });
};

async function validateScripts (appletDesignVersionConfig: Latest.AppletDesignVersionConfig): Promise<ValidationResult> {
    const allAppletVariables = getAllAppletVariables(appletDesignVersionConfig);
    for (const scriptCfg of appletDesignVersionConfig.scripts) {
        if (!scriptCfg.disableGrammarValidation) {
            if (!scriptCfg.id) {
                return {
                    status: 'error',
                    routeName: scriptsTabRoute,
                    errorMessage: messages.mes.errors.appConfig.scripts.noId,
                    accessorActions: a => validateScriptsAccessorActions(a, scriptCfg.id),
                };
            }

            if (!scriptCfg.script) {
                return {
                    status: 'error',
                    routeName: scriptsTabRoute,
                    errorMessage: messages.mes.errors.appConfig.scripts.notFound,
                    accessorActions: a => validateScriptsAccessorActions(a, scriptCfg.id),
                };
            }

            const {
                modelMarkers,
            } = await validateJavascript(scriptCfg.script, allAppletVariables);

            if (modelMarkers.length > 0) {
                return {
                    status: 'error',
                    routeName: scriptsTabRoute,
                    errorMessage: `argo-script.js:${modelMarkers[0].startLineNumber}:${modelMarkers[0].startColumn} -> ${JSON.stringify(modelMarkers[0])}`,
                    accessorActions: accessor => {
                        accessor.appletDesign.setValidatingScripts(false);
                        accessor.appletDesign.patchAppletDesignState({ activeScriptId: scriptCfg.id });
                    },
                };
            } else {
                console.log('No errors found in script');
            }

            const varsInUse = getReferencedAppletVars(scriptCfg.script);
            const invalidVar = varsInUse.find(varName => !Object.values(allAppletVariables).find(av => av.name === varName));
            if (invalidVar) {
                return {
                    status: 'error',
                    routeName: scriptsTabRoute,
                    errorMessage: messages.mes.errors.appConfig.scripts.varNotFound(invalidVar),
                    accessorActions: accessor => {
                        accessor.appletDesign.setValidatingScripts(false);
                        accessor.appletDesign.patchAppletDesignState({ activeScriptId: scriptCfg.id });
                    },
                };
            }
        }
    }

    return {
        status: 'success',
    };
}

function validateEvents (appletDesignVersionConfig: Latest.AppletDesignVersionConfig): ValidationResult {
    const events = getAppletEvents(appletDesignVersionConfig);
    const usedEventNames: Record<string, string> = {};

    for (const [ eventId, event ] of Object.entries(events)) {
        if (usedEventNames[event.def.appletEventName]) {
            const cellName = findCellInCfg(appletDesignVersionConfig, cell => event.source.kind === 'btn' ? cell.id === event.source.cellId : false)?.cellCfg.name || 'Unnamed';
            const cellTitle = cellName ? 'Button' : '';
            return {
                status: 'error',
                routeName: screensTabRoute,
                errorMessage: messages.mes.errors.appConfig.duplicateAppletEventName({ eventName: event.def.appletEventName, cellName, cellTitle }),
            };
        }
        usedEventNames[event.def.appletEventName] = eventId;
    }
    return {
        status: 'success',
    };
}

const validateConnections = <KIND extends NodeKind>(node: NodeCfg<KIND>, connections: NodeConnections<KIND>): true | {[socketName: string]: string} | Promise<true | {[socketName: string]: string}> => {
    const requiredConnections = nodeKindMap[node.kind].requiredSockets;
    if (!requiredConnections || requiredConnections === 'none') {
        return true;
    }

    const errors: {
        [socketName: string]: string;
    } = {};
    const nodeKind = node.kind;
    const nodeCfg = node as NodeCfg<typeof nodeKind>;
    const nodeKindItemMap = nodeKindMap[nodeKind] as UiNodeSpec<typeof nodeKind>;
    const availableInputSockets = Object.keys(nodeKindItemMap.inputs(nodeCfg));
    const availableOutputSockets = Object.keys(nodeKindItemMap.outputs(nodeCfg));
    const allGeneratedConnections = [
        ...availableInputSockets,
        ...availableOutputSockets,
    ];
    if (typeof requiredConnections === 'function') {
        const connectionValidator = requiredConnections as RequiredSockets<typeof node.kind, NodeSocketKindMap[typeof node.kind]>;
        const requiredConnectionsResult = connectionValidator(nodeCfg, availableInputSockets as (keyof NodeSocketKindMap[typeof node.kind]['inputs'])[], availableOutputSockets as (keyof NodeSocketKindMap[typeof node.kind]['outputs'])[]);
        for (const key of requiredConnectionsResult.required) {
            if (!(key in connections.inputs) && !(key in connections.outputs) && (node.socketDefaults?.inputs || {})[key] === undefined && (node.socketDefaults?.outputs || {})[key] === undefined) {
                errors[String(key)] = 'Connection is required';
            }
        }
        for (const conditionalSocket of requiredConnectionsResult.conditionallyRequired) {
            let isError = true;
            if (conditionalSocket.kind === 'or') {
                for (const conditionalSocketName of conditionalSocket.availableOptions) {
                    if (conditionalSocketName in connections.inputs || conditionalSocketName in connections.outputs) {
                        isError = false;
                    }
                }
            } else if (conditionalSocket.kind === 'and') {
                isError = false;
                for (const conditionalSocketName of conditionalSocket.availableOptions) {
                    if (!(conditionalSocketName in connections.inputs) && !(conditionalSocketName in connections.outputs)) {
                        isError = true;
                    }
                }
            } else {
                const u: never = conditionalSocket;
                return {
                    unknown: `Unknown conditional socket kind: ${u}`,
                };
            }
            if (isError) {
                const orErrorMsg = `At least one of the following socket connections is requied: ${conditionalSocket.availableOptions.join(', ')}`;
                for (const key of conditionalSocket.availableOptions) {
                    if (conditionalSocket.kind === 'and') {
                        errors[String(key)] = 'Connection is required';
                    } else {
                        errors[String(key)] = orErrorMsg;
                    }
                }
            }
        }
    } else if (requiredConnections === 'all') {
        for (const key of allGeneratedConnections) {
            if (!(key in connections.inputs) && !(key in connections.outputs) && (node.socketDefaults?.inputs || {})[key] === undefined && (node.socketDefaults?.outputs || {})[key] === undefined) {
                errors[String(key)] = 'Connection is required';
            }
        }
    } else {
        for (const key of requiredConnections) {
            if (!(key in connections.inputs) && !(key in connections.outputs) && (node.socketDefaults?.inputs || {})[key] === undefined && (node.socketDefaults?.outputs || {})[key] === undefined) {
                errors[String(key)] = 'Connection is required';
            }
        }
    }
    return Object.keys(errors).length ? errors : true;
};

const validateFlowsAccessorActions = (accessor: Parameters<NonNullable<(ValidationResult & { status: 'error'})['accessorActions']>>[0], flowId: string) => {
    accessor.appletDesign.patchAppletDesignState({ activeFlowId: flowId });
};

async function validateFlows (appletDesignVersionConfig: Latest.AppletDesignVersionConfig, ignoreFunctionalFlows: boolean, featureFlags: Partial<FeatureFlags>): Promise<ValidationResult> {
    const flows = appletDesignVersionConfig.flows;
    for (const [ flowId, flow ] of Object.entries(flows)) {
        if (flow.kind === 'functional') {
            const otherFlowThatUsesThisFunctionalFlow = Object.values(flows).find(f => Object.values(f.nodes).find(n => n.kind === 'functionalSelect' && (n as NodeCfg<'functionalSelect'>).controlValues.flowId === flowId));
            if (!otherFlowThatUsesThisFunctionalFlow && !ignoreFunctionalFlows) {
                return {
                    status: 'modal',
                    modalComponent: () => import(/* webpackChunkName: "appletDesign" */ '../screens/screen-preview/warningDialogs/FunctionalFlowNotUsedWarning.view.vue').then(m => m.default),
                };
            }


            // this node is managed by us, so I dont expect it to be deleted, this is more of a sanity check
            if (!Object.values(flow.nodes).find(n => n.kind === 'functionalInputs')) {
                return {
                    status: 'error',
                    routeName: flowsTabRoute,
                    errorMessage: 'Funtional flows need a functional inputs node',
                    accessorActions: a => validateFlowsAccessorActions(a, flowId),
                };
            }

            const functionalOutputsNodes = Object.values(flow.nodes).filter((n): n is NodeCfg<'functionalOutputs'> => n.kind === 'functionalOutputs');
            // this node CAN be deleted so this check is crucial
            if (!functionalOutputsNodes.length) {
                return {
                    status: 'error',
                    routeName: flowsTabRoute,
                    errorMessage: 'Funtional flows need at least one functional outputs node',
                    accessorActions: a => validateFlowsAccessorActions(a, flowId),
                };
            }
            // using essentially a random functional outputs node to validate all functional outputs nodes are the same
            const templateFunctionalOutputsNode = functionalOutputsNodes.pop();
            if (!templateFunctionalOutputsNode) {
                return {
                    status: 'error',
                    routeName: flowsTabRoute,
                    errorMessage: 'Funtional flows need at least one functional outputs node',
                    accessorActions: a => validateFlowsAccessorActions(a, flowId),
                };
            }
            for (const functionalOutputNode of functionalOutputsNodes) {
                for (const output of functionalOutputNode.controlValues.outputs) {
                    const existingOutput = templateFunctionalOutputsNode.controlValues.outputs.find(o => o.dataType === output.dataType && o.name === output.name);
                    if (!existingOutput) {
                        return {
                            status: 'error',
                            routeName: flowsTabRoute,
                            errorMessage: `Funtional outputs node "${functionalOutputNode.label}" has output values that do not match other functional outputs nodes`,
                            accessorActions: a => validateFlowsAccessorActions(a, flowId),
                        };
                    }
                }
            }
        }

        // instead of each node's validator searching the flow for their relevant connections,
        // we can prepare all of them with a single pass.
        const connectionsPerNode: Record<string, WideNodeConnections> = Object.keys(flow.nodes).reduce((acc, curr) => {
            acc[curr] = {
                inputs: {},
                outputs: {},
            };
            return acc;
        }, {} as Record<string, WideNodeConnections>);
        for (const conn of Object.values(flow.connections)) {
            if (!connectionsPerNode[conn.sourceNodeId].outputs[conn.sourceNodePort]) {
                connectionsPerNode[conn.sourceNodeId].outputs[conn.sourceNodePort] = [];
            }
            if (!connectionsPerNode[conn.targetNodeId].inputs[conn.targetNodePort]) {
                connectionsPerNode[conn.targetNodeId].inputs[conn.targetNodePort] = [];
            }

            connectionsPerNode[conn.sourceNodeId].outputs[conn.sourceNodePort]?.push(conn);
            connectionsPerNode[conn.targetNodeId].inputs[conn.targetNodePort]?.push(conn);
        }

        for (const [ nodeId, nodeConnections ] of Object.entries(connectionsPerNode)) {
            // NOTE: We only have to check inputs since every input will have a corresponding output. Also checking outputs would be redundant at that point
            for (const [ inputKey, inputConnections ] of Object.entries(nodeConnections.inputs)) {
                for (const inputConnection of inputConnections || []) {
                    const connectionCfg = Object.entries(flow.connections).find(([ , c ]) => c.sourceNodeId === inputConnection.sourceNodeId && c.targetNodeId === inputConnection.targetNodeId && c.sourceNodePort === inputConnection.sourceNodePort && c.targetNodePort === inputConnection.targetNodePort);
                    const targetNodeCfg = flow.nodes[inputConnection.targetNodeId];
                    const sourceNodeCfg = flow.nodes[inputConnection.sourceNodeId];
                    if (!targetNodeCfg || !sourceNodeCfg || !connectionCfg) {
                        return {
                            status: 'error',
                            routeName: flowsTabRoute,
                            errorMessage: `Target node or source node not found for a connection. Received: ${JSON.stringify(inputConnection)} for input key "${inputKey}" when checking the input sockets for node id "${nodeId}"`,
                        };
                    }
                    const [ connectionId ] = connectionCfg;
                    const typedTargetNodeCfg = targetNodeCfg as NodeCfg<typeof targetNodeCfg.kind>;
                    const targetNodeKindItemMap = nodeKindMap[targetNodeCfg.kind] as UiNodeSpec<typeof targetNodeCfg.kind>;
                    const targetNodeInputSocket = targetNodeKindItemMap.inputs(typedTargetNodeCfg) as Record<string, UiFlowNodeInput.Opts<NodeSocketKind>>;
                    const targetNodeInput = targetNodeInputSocket[inputConnection.targetNodePort];

                    const typedSourceNodeCfg = sourceNodeCfg as NodeCfg<typeof sourceNodeCfg.kind>;
                    const sourceNodeKindItemMap = nodeKindMap[sourceNodeCfg.kind] as UiNodeSpec<typeof sourceNodeCfg.kind>;
                    const sourceNodeOutputs = sourceNodeKindItemMap.outputs(typedSourceNodeCfg) as Record<string, UiFlowNodeOutput.Opts<NodeSocketKind>>;
                    const sourceNodeOutput = sourceNodeOutputs[inputConnection.sourceNodePort];

                    if (!UiFlowNodeSocket.isCompatibleWith(sourceNodeOutput.socket, targetNodeInput.socket) && !featureFlags.unstableRelaxedValidationForLegacyApplets) {
                        const errorMessage = `Connection "${connectionId}" is not valid. Input and output are not compatible. Input is "${targetNodeInput.socket}". Output is "${sourceNodeOutput.socket}"`;
                        return {
                            errorMessage,
                            status: 'error',
                            routeName: flowsTabRoute,
                            accessorActions: accessor => {
                                accessor.appletDesign.setInvalidConnections({ flowId, connectionId, message: errorMessage });
                            },
                        };
                    }
                }
            }
        }

        for (const node of Object.values(flow.nodes)) {
            if (!node.disabled) {
                const nodeKind = node.kind;
                const nodeKindMapItem = nodeKindMap[nodeKind];
                const nodeValidator = nodeKindMapItem.validator as NodeValidation<typeof nodeKind>;
    
                // TODO I was able to avoid type casing the connectionsPerNode because it was an interface (I think)... could I do the same with `NodeCfg`?
                const validateResult = await nodeValidator(node as NodeCfg<typeof nodeKind>, appletDesignVersionConfig);
                const validateConnectionResult = await validateConnections<typeof nodeKind>(node as NodeCfg<typeof nodeKind>, connectionsPerNode[node.id]);

                let areErrors: Record<string, string> = {};
                if (typeof validateResult === 'object') {
                    areErrors = {
                        ...areErrors,
                        ...validateResult,
                    };
                }
                if (typeof validateConnectionResult === 'object') {
                    areErrors = {
                        ...areErrors,
                        ...validateConnectionResult,
                    };
                }
                const errors = Object.entries(areErrors);
                if (errors.length) {
                    const [ controlName, errMsg ] = errors[0];
                    const nodeLabel = node.label
                        ? `"${node.label}"`
                        : nodeKindMapItem.kindLabel;
                    return {
                        status: 'error',
                        errorMessage: `${nodeLabel} - ${errMsg}`,
                        routeName: flowsTabRoute,
                        accessorActions: accessor => {
                            accessor.appletDesign.setInvalidNodes({ flowId, nodeId: node.id, controlName, message: errMsg });
                            accessor.appletDesign.patchAppletDesignState({ activeFlowId: flowId });
                        },
                    };
                }
            }
        }
    }
    return {
        status: 'success',
    };
}

async function validateScreens (appletDesignConfig: Latest.AppletDesignVersionConfig, ignoreUnlinkedCells: boolean): Promise<ValidationResult> {
    const screens = appletDesignConfig.screens;
    const allAppletVariables = getAllAppletVariables(appletDesignConfig);
    for (const screen of screens) {
        for (const [ tempAspectRatio, gridLayout ] of Object.entries(screen.gridLayoutAspectRatios)) {
            const aspectRatio = tempAspectRatio as Latest.Screen.Designer.GridLayoutAspectRatio;
            const result = genericScreenDesignerCellValidations({
                screen,
                aspectRatio,
                allAppletVariables,
                cellItem: gridLayout.gridConfig,
            });
            if (result.status === 'error') {
                return result;
            }
            for (const lgc of gridLayout.linkedGridCells) {
                const lgcResult = genericScreenDesignerCellValidations({
                    screen,
                    aspectRatio,
                    cellItem: lgc,
                    allAppletVariables,
                });
                if (lgcResult.status === 'error') {
                    return lgcResult;
                }
            }
        }

        const cells = screen.cells;
        for (const cell of cells) {
            if (cell.modifiers) {
                Object.entries(cell.modifiers).forEach(([ _, modVal ]) => {
                    if (modVal && !allAppletVariables[modVal]) {
                        return {
                            status: 'error',
                            errorMessage: messages.mes.errors.appConfig.nonexistentVarCell({
                                cellLabel: cell.name,
                                varId: modVal,
                                cellType: cell.type,
                            }),
                        };
                    }
                });
            }
            const validator = cellTypeMap[cell.type].validator;
            if (validator) {
                const cellValidatorResult = await validator(cell, appletDesignConfig);
                if (cellValidatorResult.status === 'error') {
                    return {
                        status: 'error',
                        routeName: screensTabRoute,
                        errorMessage: cellValidatorResult.errorMessage,
                        accessorActions: accessor => {
                            accessor.appletDesign.patchAppletDesignState({
                                activeScreenId: screen.id,
                                activeCellIdPerScreen: {
                                    ...accessor.appletDesign.activeCellIdPerScreen,
                                    [screen.id]: cell.id,
                                },
                            });
                        },
                    };
                }
            }

            if (getUnlinkedCell({ screen }) && !ignoreUnlinkedCells) {
                return {
                    status: 'modal',
                    modalComponent: () => import(/* webpackChunkName: "material" */ '../screens/screen-preview/warningDialogs/ScreenSaveWarning.view.vue').then(m => m.default),
                };
            }
        }
        if (Object.values(screen.gridLayoutAspectRatios).findIndex(gridLayout => gridLayout.enabled) === -1) {
            return {
                status: 'error',
                routeName: screensTabRoute,
                errorMessage: messages.mes.errors.appConfig.noAspectRatio,
                accessorActions: accessor => {
                    accessor.appletDesign.patchAppletDesignState({
                        activeScreenId: screen.id,
                    });
                },
            };
        }
    }
    return {
        status: 'success',
    };
}

function validateMacros (entityData: EntityTypeMap['appletDesignVersion']): ValidationResult {
    for (const macro of entityData.config.macros || []) {
        switch (macro.type) {
            case 'materialLookup': {
                const macroValidationResult = macroTypeMap.materialLookup.validator(macro, entityData);
                if (macroValidationResult.status === 'error') {
                    return {
                        status: 'error',
                        routeName: screensTabRoute,
                        errorMessage: macroValidationResult.errorMessage,
                        accessorActions: accessor => {
                            const cellTarget = findCellInCfg<Latest.Screen.Cells.MacroTargetCell>(entityData.config, cell => cell.type === 'macroTarget' && cell.attrs.macroId === macro.id);
                            if (!cellTarget) {
                                return;
                            }
                            accessor.appletDesign.patchAppletDesignState({
                                activeScreenId: cellTarget.screenId,
                                activeCellIdPerScreen: {
                                    ...accessor.appletDesign.activeCellIdPerScreen,
                                    [cellTarget.screenId]: cellTarget.cellCfg.id,
                                },
                            });
                        },
                    };
                }
                break;
            }
            default: {
                const unknownType: never = macro.type;
                return {
                    status: 'error',
                    errorMessage: `Unknown macro type "${unknownType}"`,
                };
            }
        }
    }
    return {
        status: 'success',
    };
}

export async function validateAppletDesignVersion (opts: {
    pendingMedia: PendingMediaObjectReference[];
    entityData: EntityTypeMap['appletDesignVersion'];
    /** for conditional validation. `Partial` because it's `Partial` upstream */
    featureFlags: Partial<FeatureFlags>;
}): Promise<ValidationResult> {
    if (!opts.entityData.design.name) {
        return {
            status: 'error',
            routeName: generalTabRoute,
            errorMessage: messages.mes.errors.appConfig.name,
        };
    }

    if (Object.keys(opts.pendingMedia).length > 0) {
        return Promise.resolve({
            status: 'modal',
            modalComponent: () => import(/* webpackChunkName: "material" */ '../../../media/MediaUploadDialog.vue').then(m => m.default),
            modalProps: {
                pendingMedia: opts.pendingMedia,
                updateEntity: (payload: MediaUploadDialogPayload) => {
                    if (!opts.entityData) {
                        return;
                    }

                    payload.accessor.setPageEntity({
                        type: 'appletDesignVersion',
                        entity: {
                            ...opts.entityData,
                            config: {
                                ...opts.entityData.config,
                                screens: opts.entityData.config.screens.map(screen => ({
                                    ...screen,
                                    cells: screen.cells.map<Latest.Screen.Cells.AppletCell>(cell => {
                                        if (cell.type === 'media') {
                                            return {
                                                ...cell,
                                                attrs: {
                                                    ...cell.attrs,
                                                    items: cell.attrs.items.map(item => {
                                                        const media = payload.savedMedia.find(m => item.media.type === 'pending' && m.file.id === item.media.id);
                                                        if (media) {
                                                            console.log(`updating media for cell ${cell.id} in screen ${screen.id}`, media);
                                                            return {
                                                                ...item,
                                                                media,
                                                            };
                                                        }
                                                        return item;
                                                    }),
                                                },
                                            };
                                        } else {
                                            return cell;
                                        }
                                    }),
                                })),
                            },
                        },
                    });
                    payload.accessor.clearPendingMedia();
                },
            },
        });
    }

    // validate macros
    const macroValidateGoToTab = validateMacros(opts.entityData);
    if (macroValidateGoToTab.status !== 'success') {
        return macroValidateGoToTab;
    }

    // validate screens
    const screensValidateGoToTab = await validateScreens(opts.entityData.config, opts.entityData.ignoreUnlinkedCells);
    if (screensValidateGoToTab.status !== 'success') {
        return screensValidateGoToTab;
    }

    // validate scripts
    const scriptsValidateGoToTab = await validateScripts(opts.entityData.config);
    if (scriptsValidateGoToTab.status !== 'success') {
        return scriptsValidateGoToTab;
    }

    const eventsValidateGoToTab = validateEvents(opts.entityData.config);
    if (eventsValidateGoToTab.status !== 'success') {
        return eventsValidateGoToTab;
    }
    const flowsValidateGoToTab = await validateFlows(opts.entityData.config, opts.entityData.ignoreFunctionalFlows, opts.featureFlags);
    if (flowsValidateGoToTab.status !== 'success') {
        return flowsValidateGoToTab;
    }

    // validate deploy params
    // keyed by deploy param id
    const seenDepParams: Record<string, DeployParamKind> = {};
    const deployParams = getDeployParams(opts.entityData.config, appletDesignVersionConfigSchemaLatest);
    for (const deployParam of deployParams) {
        const seenDeployParam = seenDepParams[deployParam.deployParamId];
        if (seenDeployParam && seenDeployParam !== deployParam.deployParamKind) {
            return {
                status: 'error',
                errorMessage: `Found multiple deploy params with the same id, but different kinds. Deploy param "${deployParam.deployParamId}" is both "${seenDeployParam}" and "${deployParam.deployParamKind}"`,
            };
        }
        seenDepParams[deployParam.deployParamId] = deployParam.deployParamKind;
    }

    return {
        status: 'success',
    };
}
